import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  fetchForms,
  deleteForm,
  updateForm,
  getCaFormById,
  getCaFormResponses,
} from "../utils/api"
import FormView from "./FormView"
import { RiFileTextLine, RiSurveyLine, RiListCheck2, RiQuestionnaireLine, RiFeedbackLine, RiContactsLine, RiUserLine, RiStoreLine, RiShoppingCartLine, RiServiceLine, RiSettingsLine, RiDashboardLine,  } from "react-icons/ri"
import { ChevronDown } from "lucide-react"

const iconMap = {
  RiFileTextLine,
  RiSurveyLine,
  RiListCheck2,
  RiQuestionnaireLine,
  RiFeedbackLine,
  RiContactsLine,
  RiUserLine,
  RiStoreLine,
  RiShoppingCartLine,
  RiServiceLine,
  RiSettingsLine,
  RiDashboardLine,
};

function FormIcon({ iconName, className = "w-6 h-6" }) {
  const IconComponent = iconMap[iconName];
  return IconComponent ? (
    <IconComponent className={className} />
  ) : (
    <RiFileTextLine className={className} />
  );
}

function IconPicker({ selectedIcon, onSelect }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50 w-full"
      >
        <FormIcon iconName={selectedIcon} />
        <span className="text-sm">{selectedIcon}</span>
        <ChevronDown className="w-4 h-4 ml-auto" />
      </button>
      {isOpen && (
        <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 w-full grid grid-cols-4 gap-2">
          {Object.keys(iconMap).map((icon) => (
            <button
              key={icon}
              type="button"
              onClick={() => {
                onSelect(icon);
                setIsOpen(false);
              }}
              className={`p-2 rounded-lg flex flex-col items-center ${
                selectedIcon === icon ? "bg-blue-100" : "hover:bg-gray-100"
              }`}
              title={icon}
            >
              <FormIcon iconName={icon} className="w-5 h-5" />
              <span className="text-xs mt-1 truncate w-full">{icon}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const FormList = () => {
  const [forms, setForms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedFormId, setSelectedFormId] = useState(null)
  const [formToEdit, setFormToEdit] = useState(null)
  const [formToDelete, setFormToDelete] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [viewing, setViewing] = useState({
    show: false,
    title: "",
    fields: [],
    responses: [],
  })
  const navigate = useNavigate()

  const loadForms = async () => {
    setLoading(true)
    try {
      const data = await fetchForms()
      setForms(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadForms()
  }, [])

  const handleEdit = (form) => setFormToEdit({...form})
  const handleDeleteClick = (form) => setFormToDelete(form)

  const confirmDelete = async () => {
    try {
      await deleteForm(formToDelete._id)
      loadForms()
      setFormToDelete(null)
    } catch (err) {
      alert(err.message)
    }
  }

  const handleSaveEdit = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      await updateForm(formToEdit._id, {
        name: formToEdit.name,
        enable: formToEdit.enable,
        icon: formToEdit.icon,
        thank_you_message: formToEdit.thank_you_message
      })
      await loadForms()
      setFormToEdit(null)
    } catch (err) {
      alert(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleViewResponses = async (formId) => {
    try {
      const formData = await getCaFormById(formId)
      const responses = await getCaFormResponses(formId)
      setViewing({
        show: true,
        title: formData.form.name,
        fields: formData.fields,
        responses,
      })
    } catch (err) {
      alert(err.message)
    }
  }

  if (loading)
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600">Loading forms...</p>
          </div>
        </div>
      </div>
    )
  if (error)
    return (
      <div className="container mx-auto p-6">
        <div className="bg-white rounded-lg border border-red-200 shadow-sm">
          <div className="p-6">
            <p className="text-red-600">Error: {error}</p>
          </div>
        </div>
      </div>
    )

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Forms</h1>
          <p className="text-gray-600">Manage your created forms</p>
        </div>
        <button
          onClick={() => navigate("/form-builder")}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create New Form
        </button>
      </div>

      {/* Forms Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">All Forms</h2>
          <p className="text-sm text-gray-600 mt-1">
            {forms.length} form{forms.length !== 1 ? "s" : ""} total
          </p>
        </div>
        <div className="p-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Icon
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Form Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submissions
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {forms.map((form, idx) => (
                <tr key={form._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <FormIcon iconName={form.icon} className="w-5 h-5" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {idx + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{form.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        form.enable
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {form.enable ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(form.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => handleViewResponses(form._id)}
                      title="View Submissions"
                      className="p-1"
                    >
                      <svg
                        className="h-5 w-5 text-indigo-600 hover:text-indigo-900"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v16H4V4z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9h8M8 13h8M8 17h8" />
                      </svg>
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex justify-end gap-2">
                    <button
                      onClick={() => setSelectedFormId(form._id)}
                      title="Preview"
                      className="p-1"
                    >
                      <svg
                        className="h-5 w-5 text-blue-600 hover:text-blue-900"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                    <button onClick={() => handleEdit(form)} title="Edit" className="p-1">
                      <svg
                        className="h-5 w-5 text-green-600 hover:text-green-900"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1-9l-9 9" />
                      </svg>
                    </button>
                    <button onClick={() => handleDeleteClick(form)} title="Delete" className="p-1">
                      <svg
                        className="h-5 w-5 text-red-600 hover:text-red-900"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: Preview Form */}
      {selectedFormId && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedFormId(null)}
        >
          <div
            className="bg-white w-full max-w-4xl rounded-lg shadow-xl overflow-hidden max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-semibold">Form Preview</h3>
              <button
                onClick={() => setSelectedFormId(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <FormView id={selectedFormId} isModal />
            </div>
          </div>
        </div>
      )}

      {/* Modal: Edit Form */}
      {formToEdit && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setFormToEdit(null)}
        >
          <div
            className="bg-white w-full max-w-md rounded-lg shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Edit Form</h3>
                <p className="text-sm text-gray-600 mt-1">Update form details</p>
              </div>
              <button
                onClick={() => setFormToEdit(null)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded"
                disabled={isSaving}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleSaveEdit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Form Name
                    </label>
                    <input
                      type="text"
                      value={formToEdit.name}
                      onChange={(e) =>
                        setFormToEdit({ ...formToEdit, name: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Form name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Form Icon
                    </label>
                    <IconPicker
                      selectedIcon={formToEdit.icon}
                      onSelect={(icon) => setFormToEdit({ ...formToEdit, icon })}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Thank You Message
                    </label>
                    <textarea
                      value={formToEdit.thank_you_message || ""}
                      onChange={(e) =>
                        setFormToEdit({ ...formToEdit, thank_you_message: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Thank you message to show after form submission"
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enable"
                      checked={formToEdit.enable}
                      onChange={(e) =>
                        setFormToEdit({ ...formToEdit, enable: e.target.checked })
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enable" className="ml-2 block text-sm text-gray-700">
                      Active
                    </label>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setFormToEdit(null)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center justify-center"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Delete Confirmation */}
      {formToDelete && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setFormToDelete(null)}
        >
          <div
            className="bg-white w-full max-w-md rounded-lg shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg
                    className="h-6 w-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Delete form</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete "{formToDelete.name}"? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setFormToDelete(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: View Submissions */}
      {viewing.show && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setViewing((v) => ({ ...v, show: false }))}
        >
          <div
            className="bg-white w-full max-w-4xl rounded-lg shadow-xl overflow-auto max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold">Submissions: {viewing.title}</h3>
            </div>
            <div className="p-6 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      S.No
                    </th>
                    {viewing.fields.map((f) => (
                      <th
                        key={f._id}
                        className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {f.name || `Field ${f.order}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {viewing.responses.map((resp, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900">{i + 1}</td>
                      {viewing.fields.map((f) => (
                        <td key={f._id} className="px-4 py-2 text-sm text-gray-900">
                          {Array.isArray(resp[f._id]) ? resp[f._id].join(", ") : resp[f._id]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 flex justify-end">
              <button
                onClick={() => setViewing((v) => ({ ...v, show: false }))}
                className="px-4 py-2 bg-blue-600 text-white rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FormList